import { useCallback, useMemo } from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import { Task } from "@common/types/task";
import type { TaskBulkUpdateAction } from "@common/schemas/task.schema";
import { useBulkUpdateTasksAcrossWorkspaces } from "@api/task.api";
import { useCommands } from "@components/commands/useCommands";
import { CommandsDirectory } from "@components/commands/lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    buildTaskBulkPriorityOptions,
    buildTaskBulkStatusOptions,
} from "./taskBulkCommandOptions";
import { useTaskBulkActionsOpenOverride } from "./useTaskBulkActionsOpenOverride";

interface UseOrganizationTaskBulkActionsParams {
    selectedTasks: Task[];
    setSelectedTaskIds: (taskIds: string[]) => void;
}

/**
 * Set status and Set priority for the organization Tasks page's selected
 * rows, which can belong to different client workspaces. Offered from the
 * footer and the command palette; the workspace-only actions (assign, group,
 * entity, delete) are left out. Tasks that failed to update stay selected.
 */
export function useOrganizationTaskBulkActions({
    selectedTasks,
    setSelectedTaskIds,
}: UseOrganizationTaskBulkActionsParams) {
    const { open: openCommands } = useCommands();
    const { error: showErrorToast } = useToaster();
    const { mutate, isPending } = useBulkUpdateTasksAcrossWorkspaces();

    const applyAction = useCallback(
        (action: TaskBulkUpdateAction) => {
            mutate(
                { tasks: selectedTasks, action },
                {
                    onSuccess: ({ updatedTaskIds, failedTaskIds }) => {
                        if (
                            failedTaskIds.length > 0 &&
                            updatedTaskIds.length > 0
                        ) {
                            showErrorToast(
                                `Updated ${pluralizeWithCount("task", updatedTaskIds.length)}, ${failedTaskIds.length} failed`,
                            );
                        } else if (failedTaskIds.length > 0) {
                            showErrorToast(
                                `Failed to update ${pluralizeWithCount("task", failedTaskIds.length)}`,
                            );
                        }
                        setSelectedTaskIds(failedTaskIds);
                    },
                    onError: () => showErrorToast("Failed to update tasks"),
                },
            );
        },
        [mutate, selectedTasks, setSelectedTaskIds, showErrorToast],
    );

    const statusOptions = useMemo(
        () => buildTaskBulkStatusOptions({ isPending, applyAction }),
        [applyAction, isPending],
    );

    const priorityOptions = useMemo(
        () => buildTaskBulkPriorityOptions({ isPending, applyAction }),
        [applyAction, isPending],
    );

    const selectedCount = selectedTasks.length;

    const handleSetStatusClick = useCallback(() => {
        if (!selectedCount) {
            return;
        }
        openCommands(CommandsDirectory.TASK_BULK_STATUS, {
            taskBulkStatusOptions: statusOptions,
            selectedTaskCount: selectedCount,
        });
    }, [openCommands, selectedCount, statusOptions]);

    const handleOpenBulkActions = useCallback(() => {
        openCommands(CommandsDirectory.TASK_BULK, {
            taskBulkStatusOptions: statusOptions,
            taskBulkPriorityOptions: priorityOptions,
            selectedTaskCount: selectedCount,
        });
    }, [openCommands, statusOptions, priorityOptions, selectedCount]);

    useTaskBulkActionsOpenOverride({
        selectedCount,
        openBulkActions: handleOpenBulkActions,
    });

    return { handleSetStatusClick, handleOpenBulkActions };
}
